import { environment } from "../../../environments/environment";
import { Component, OnInit, HostListener, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { IonSearchbar, IonTabBar, IonTabButton, IonIcon, IonLabel, IonBadge } from '@ionic/angular';
import { ModalController } from "@ionic/angular";
// import { GuestlogModal } from "@/_modals/guestlog-modal/guestlog-modal";
import { Animations } from "@/_shared/animations";
import { AuthService, StorageService } from '@/_services';
import { CurrentUser, Service, RequestedService } from '@/_models';
import { serviceOptions } from "@/_shared/consts/services";

type ServiceListItem =
	| {
			type: 'category';
			categoryId: number | null;
			categoryName: string;
			services: RequestedService[];
	  }
	| {
			type: 'service';
			service: RequestedService;
	  };

@Component({
	selector: 'app-select-service-modal',
	templateUrl: './select-service-modal.html',
	styleUrls: ['./select-service-modal.scss'],
	animations: [ Animations ],
	providers : [ TranslateService ]
})
export class SelectServiceModal implements OnInit {
	@ViewChild('searchContainer', { static: true }) searchContainer!: ElementRef;
	@ViewChild('searchbar', { static: true }) searchbar!: IonSearchbar;

	BASE_URL = environment.base_url;
	currentUser: CurrentUser | null;

	filteredServices: RequestedService[] = [];
	searchString: string = '';
	serviceOptions!: RequestedService[];

	preServices!: RequestedService[];
	selectedServices: RequestedService[] = [];
	
	displayItems: any[] = [];
	expandedCategories = new Set<number>();

	private quantityBlurTimers = new WeakMap<any, any>();

	@HostListener("window:keyboardWillShow", ["$event"])
	keyboardWillShow(event?: Event) {
		console.log("Keyboard will Show");
	}
	@HostListener("window:keyboardDidShow", ["$event"])
	keyboardDidShow(event?: Event) {
		console.log("Keyboard did Show");
	}
	
	constructor(
		private auth: AuthService,
		public viewCtrl: ModalController,
		private storage: StorageService,
		private translateService: TranslateService
	) { 
		this.currentUser = this.auth.currentUser.value;
		this.translateService.setDefaultLang(this.currentUser!.preference.displayLanguage);
	}

	ngOnInit() {
		const fs = this.serviceOptions;
		this.filteredServices = structuredClone(this.serviceOptions);
		if (this.categorizeServices) this.buildCategorizedList();

		this.selectedServices = JSON.parse(JSON.stringify(this.preServices));

		const categoryIds = this.displayItems
			.filter(item => item.type === 'category')
			.map(item => item.categoryId);
		categoryIds.forEach(id => {
				if (this.isSelectedCategory(id)) this.expandedCategories.add(id)
		});
		// console.log('serviceOptions', this.serviceOptions);
	}
	setFilteredServices() {
		this.filteredServices = this.filterServices(this.searchString);
		if (this.categorizeServices) this.buildCategorizedList();
	}
	filterServices(searchString: string = '') {
		const search = searchString.toLowerCase().trim();

		if (!search) return [...this.serviceOptions];

		return this.serviceOptions.filter(service => {
			const name = service.name.toLowerCase();
			return (
				name.startsWith(search) ||
				name.includes(' ' + search)
			);
		});
	}
	buildCategorizedList() {
		const categorized = new Map<number, any>();
		const uncategorized: any[] = [];

		for (const service of this.filteredServices) {

			// No category
			if (!service.type) {
				uncategorized.push({
					type: 'service',
					service
				});
				continue;
			}

			const categoryId = service.type.id;

			if (!categorized.has(categoryId)) {

				categorized.set(categoryId, {
					type: 'category',
					categoryId,
					categoryName: service.type.name,
					categoryDescription: service.type.description,
					services: []
				});

			}
			categorized.get(categoryId).services.push(service);
		}

		const sortedCategories = Array
			.from(categorized.values())
			.sort((a, b) =>
				a.categoryName.localeCompare(b.categoryName)
			);

		this.displayItems = [
			...sortedCategories,
			...uncategorized
		];

	}
	toggleCategory(categoryId: number) {
		if (this.expandedCategories.has(categoryId)) {
			this.expandedCategories.delete(categoryId);
		} else {
			this.expandedCategories.add(categoryId);
		}
	}
	isCollapsed(categoryId: number): boolean {
		return !this.expandedCategories.has(categoryId);
	}
	isSelectedCategory(categoryId: number): boolean {
		return this.selectedServices?.find((el: RequestedService) => el.typeId === categoryId) !== undefined;
	}

	isSelected(id: any): boolean {
		return this.selectedServices?.find((el: RequestedService) => el.id === id) !== undefined;
	}
	addRemoveService(service: RequestedService) {
		const index = this.selectedServices.findIndex(el => el.id === service.id);
		if (index === -1) {
			this.selectedServices.push({
				id: service.id,
				name: service.name,
				code: service.code,
				type: service.type,
				typeId: service.typeId,
				duration: service.duration,
				quantity: 1
			});
		} else {
			this.selectedServices.splice(index, 1);
		}
	}
	selectServices() {
		const services = this.selectedServices.length > 0
			? this.selectedServices.map((service: RequestedService) => ({
				...service,
				quantity: Math.min(15, Math.max(1, Number(service.quantity) || 1))
			}))
			: undefined;

		this.dismiss(services);
	}

	selectQuantityInput(ionInput: any, event: Event) {
		event.stopPropagation();
		setTimeout(async () => {
			const native = await ionInput.getInputElement();
			native.select();
		}, 0);
	}

	async handleQuantityEvent(event: any, service: RequestedService, ionInput: any) {
		event.stopPropagation();

		const key = event.key;

		if (key === 'Backspace' || key === 'Delete' || key === '0') {
			event.preventDefault?.();
			service.quantity = ionInput.value = 1;
			this.scheduleBlur(ionInput, service);
			return;
		}

		if (key) return;

		const native = await ionInput.getInputElement();
		service.quantity = ionInput.value = this.normalizeQuantity(native.value);

		this.scheduleBlur(ionInput, service);
	}

	async commitQuantity(ionInput: any, service: RequestedService) {
		const native = await ionInput.getInputElement();
		service.quantity = ionInput.value = this.normalizeQuantity(native.value);
	}

	private scheduleBlur(ionInput: any, service: RequestedService) {
		clearTimeout(this.quantityBlurTimers.get(ionInput));

		const timer = setTimeout(async () => {
			await this.commitQuantity(ionInput, service);

			const native = await ionInput.getInputElement();
			native.blur();
		}, 350);

		this.quantityBlurTimers.set(ionInput, timer);
	}

	private normalizeQuantity(value: any): number {
		value = Number(value);
		return !value || value < 1 ? 1 : value > 15 ? 15 : value;
	}

	getSelectedService(service: RequestedService): RequestedService | undefined {
		return this.selectedServices.find(el => el.id === service.id);
	}

	get categorizeServices(): boolean {
		return this.auth.appEntryModeSettings.value.categorizeServices.enabled;
	}

	dismiss(services?: any) {
		if (services) {
			this.viewCtrl.dismiss(services);
		} else {
			this.viewCtrl.dismiss();
		}
	}
	
}
